import { useCallback, useEffect } from 'react';
import type { AppUpdater } from '../ports/AppUpdater';
import { useAppUpdateStore, WORKER } from './appUpdateStore';

/** While the app is open and on screen. */
export const CHECK_INTERVAL_MS = 5 * 60_000;

/**
 * Focus, visibilitychange and pageshow tend to arrive together when a phone
 * comes out of a pocket. One request answers all three.
 */
export const MIN_CHECK_GAP_MS = 30_000;

/**
 * Watches for a newer build. Mounted once, in App, beside the outbox replay.
 *
 * Asks on launch, whenever the app comes back to the foreground, and every five
 * minutes while it is open. The foreground is the one that matters on the floor:
 * an app added to the home screen is resumed far more often than it is launched,
 * and iOS in particular keeps one alive for days. A check that finds a new build
 * also has the service worker fetch it, which it otherwise does only on a
 * navigation, and a resumed app does not navigate. `/version.json` is a few
 * bytes; the worker script is not, so it is only fetched when there is news.
 *
 * It offers and never reloads. Reloading under somebody's hands would throw
 * away the form they are filling in; the banner waits until they tap Update.
 */
export function useAppUpdateWatch(updater: AppUpdater, current: string, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    const { offer } = useAppUpdateStore.getState();
    let last = Number.NEGATIVE_INFINITY;
    let stopped = false;

    const check = (force: boolean) => {
      const now = Date.now();
      if (!force && now - last < MIN_CHECK_GAP_MS) return;
      last = now;

      void updater.deployedVersion().then((deployed) => {
        if (stopped || deployed === null || deployed === current) return;
        // With the release the same answer named, so the banner can say which
        // version this is. The request of 5 October 2026 (third), section 8.
        offer(deployed, updater.deployedRelease());
        // The last tap reloaded and the page came back on the old build: finish
        // the job now rather than asking for the tap again.
        if (updater.continuePending()) {
          const { applying, setApplying } = useAppUpdateStore.getState();
          if (applying) return;
          setApplying(true);
          void updater
            .apply()
            .catch(() => false)
            .then((reloading) => {
              if (!reloading) setApplying(false);
            });
          return;
        }
        // Fetched now rather than on the tap, so Update has it ready.
        updater.checkWorker();
      });
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') check(false);
    };
    const onFocus = () => check(false);
    // A page restored from the back/forward cache runs no startup code at all.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) check(false);
    };

    check(true);

    // Not while hidden: a phone in a pocket should not spend battery on this,
    // and coming back to the foreground asks anyway.
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') check(true);
    }, CHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    window.addEventListener('pageshow', onPageShow);
    const unsubscribe = updater.onNewWorker(() => offer(WORKER));

    return () => {
      stopped = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      unsubscribe();
    };
  }, [updater, current, enabled]);
}

/**
 * The Update button. Resolves true when the page is reloading onto the new
 * build, false when it could not (the banner stays and says why). A second tap
 * while the first is still working does nothing.
 */
export function useApplyUpdate(updater: AppUpdater): () => Promise<boolean> {
  return useCallback(async () => {
    const { applying, setApplying } = useAppUpdateStore.getState();
    if (applying) return false;

    setApplying(true);
    const reloading = await updater.apply().catch(() => false);
    if (!reloading) setApplying(false);
    return reloading;
  }, [updater]);
}
